/**
 * Two-question feedback popup for the For You page.
 *
 * Deliberately short: shoppers say how well we read their interests and whether
 * the books fit, with an optional comment. The full star-rating form stays on
 * the internal testing page.
 *
 * The same dialog serves the "report a problem" choice from the feedback panel,
 * where only the comment is asked for.
 */

import { Check, Loader2, X } from 'lucide-react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type BooksFitAnswer,
  submitExperienceFeedback,
  type UnderstandingAnswer,
} from '../services/api';

const UNDERSTANDING_ANSWERS: UnderstandingAnswer[] = ['very_well', 'somewhat', 'not_really'];

const BOOKS_FIT_ANSWERS: BooksFitAnswer[] = ['yes', 'some_of_them', 'not_really'];

interface ExperienceFeedbackModalProps {
  /** 'rate' asks the two questions; 'report' collects a problem description. */
  mode: 'rate' | 'report';
  shopifyCustomerId?: string;
  /** 'onboarding' for cold-start shoppers; recorded alongside the answers. */
  personalizationLevel?: string;
  onClose: () => void;
}

/**
 * "How well did we understand" and "do these books fit" take different scales,
 * so each question brings its own answers and its own labels rather than
 * sharing a yes/no set that reads wrong under one of them.
 */
function AnswerRow<T extends string>({
  question,
  scaleKey,
  answers,
  value,
  onChange,
}: {
  question: string;
  /** Translation prefix; each answer is a key beneath it. */
  scaleKey: string;
  answers: T[];
  value: T | null;
  onChange: (answer: T) => void;
}) {
  const { t } = useTranslation();

  return (
    <fieldset className="mb-5">
      <legend className="mb-2 text-sm font-medium text-indigo-brand-text">{question}</legend>
      <div className="flex gap-2">
        {answers.map((answer) => (
          <button
            key={answer}
            type="button"
            aria-pressed={value === answer}
            onClick={() => onChange(answer)}
            className={`flex-1 border px-3 py-2 text-sm transition-colors ${
              value === answer
                ? 'border-indigo-brand-black bg-indigo-brand-black text-white'
                : 'border-indigo-brand-border bg-white text-indigo-brand-text hover:bg-indigo-brand-off-white'
            }`}
          >
            {t(`${scaleKey}.${answer}`)}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

const ExperienceFeedbackModal: React.FC<ExperienceFeedbackModalProps> = ({
  mode,
  shopifyCustomerId,
  personalizationLevel,
  onClose,
}) => {
  const { t } = useTranslation();
  const [understanding, setUnderstanding] = useState<UnderstandingAnswer | null>(null);
  const [booksFit, setBooksFit] = useState<BooksFitAnswer | null>(null);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const isReport = mode === 'report';

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [onClose]);

  useEffect(() => {
    dialogRef.current?.querySelector<HTMLElement>('button, textarea')?.focus();
  }, []);

  // Keep focus inside the dialog while it is open.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return;
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
      'button, textarea, [href], input, select'
    );
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const canSubmit = isReport
    ? comment.trim().length > 0
    : understanding !== null || booksFit !== null || comment.trim().length > 0;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await submitExperienceFeedback({
        shopify_customer_id: shopifyCustomerId || '',
        understanding: isReport ? null : understanding,
        books_fit: isReport ? null : booksFit,
        comment,
        personalization_level: personalizationLevel,
      });
      setIsDone(true);
    } catch {
      setError(t('experienceFeedback.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="experience-feedback-title"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={handleKeyDown}
        className="w-full max-w-md border border-indigo-brand-border bg-white shadow-xl"
      >
        <div className="flex items-start justify-between border-b border-indigo-brand-border px-5 py-4">
          <h2
            id="experience-feedback-title"
            className="font-heading text-lg font-bold text-indigo-brand-text"
          >
            {isDone && t('experienceFeedback.doneTitle')}
            {!isDone &&
              (isReport ? t('experienceFeedback.reportTitle') : t('experienceFeedback.title'))}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="text-indigo-brand-text/50 hover:text-indigo-brand-text"
          >
            <X size={18} />
          </button>
        </div>

        {isDone ? (
          <div className="px-5 py-8 text-center">
            <span className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-indigo-brand-off-white">
              <Check size={20} className="text-indigo-brand-black" />
            </span>
            <p className="text-sm text-indigo-brand-text/70">{t('experienceFeedback.doneBody')}</p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 w-full bg-indigo-brand-black px-4 py-2.5 text-sm font-medium text-white hover:bg-black"
            >
              {t('experienceFeedback.doneCta')}
            </button>
          </div>
        ) : (
          <div className="px-5 py-5">
            {!isReport && (
              <>
                <p className="mb-5 text-sm text-indigo-brand-text/70">
                  {t('experienceFeedback.intro')}
                </p>
                <AnswerRow
                  question={t('experienceFeedback.understandingQuestion')}
                  scaleKey="experienceFeedback.understandingScale"
                  answers={UNDERSTANDING_ANSWERS}
                  value={understanding}
                  onChange={setUnderstanding}
                />
                <AnswerRow
                  question={t('experienceFeedback.booksQuestion')}
                  scaleKey="experienceFeedback.booksScale"
                  answers={BOOKS_FIT_ANSWERS}
                  value={booksFit}
                  onChange={setBooksFit}
                />
              </>
            )}

            <label
              htmlFor="experience-feedback-comment"
              className="mb-2 block text-sm font-medium text-indigo-brand-text"
            >
              {isReport
                ? t('experienceFeedback.reportLabel')
                : t('experienceFeedback.commentLabel')}
            </label>
            <textarea
              id="experience-feedback-comment"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              rows={isReport ? 5 : 3}
              placeholder={
                isReport
                  ? t('experienceFeedback.reportPlaceholder')
                  : t('experienceFeedback.commentPlaceholder')
              }
              className="w-full border border-indigo-brand-border px-3 py-2 text-sm focus:border-indigo-brand-black focus:outline-none"
            />

            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit || isSubmitting}
              className={`mt-5 flex w-full items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                canSubmit && !isSubmitting
                  ? 'bg-indigo-brand-black text-white hover:bg-black'
                  : 'cursor-not-allowed bg-indigo-brand-border text-white'
              }`}
            >
              {isSubmitting && <Loader2 size={16} className="animate-spin" />}
              {isSubmitting ? t('experienceFeedback.submitting') : t('experienceFeedback.submit')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExperienceFeedbackModal;
